import { useCallback, useEffect, useRef, useState } from "react";
import useDataset from "datasets/hooks/useDataset";
import { DatasetAccessLevel } from "datasets/types/enums/access-level";
import { Loader2, Plus } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import useErrorToast from "~/contexts/error-toast";
import SamplePromptRow from "./SamplePromptRow";

const { VITE_CATALOG_API } = import.meta.env;

type SaveState = "idle" | "saving" | "saved";

const MAX_PROMPTS = 6;
const DEBOUNCE_MS = 800;

const SamplePromptsTab = () => {
  const { data: dataset, mutate } = useDataset();
  const { showError } = useErrorToast();
  const isOwner = dataset?.access_level === DatasetAccessLevel.OWNER;

  const [prompts, setPrompts] = useState<string[]>(() => dataset?.samplePrompts ?? []);
  const [saveStates, setSaveStates] = useState<SaveState[]>(() =>
    (dataset?.samplePrompts ?? []).map(() => "idle" as SaveState)
  );

  const confirmedRef = useRef<string[]>(dataset?.samplePrompts ?? []);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Sync from server when dataset loads/changes externally
  useEffect(() => {
    if (dataset?.samplePrompts) {
      const server = dataset.samplePrompts;
      // Only sync if the confirmed ref matches what we last saved —
      // meaning there's no pending local edit
      if (JSON.stringify(confirmedRef.current) !== JSON.stringify(server)) {
        setPrompts(server);
        setSaveStates(server.map(() => "idle" as SaveState));
        confirmedRef.current = server;
      }
    }
  }, [dataset?.samplePrompts]);

  const save = useCallback(
    async (toSave: string[], changedIndices: number[]) => {
      if (!dataset) return;

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setSaveStates((prev) => {
        const next = [...prev];
        for (const i of changedIndices) {
          if (i < next.length) next[i] = "saving";
        }
        return next;
      });

      try {
        const resp = await fetch(`${VITE_CATALOG_API}/datasets/${dataset.id}/sample-prompts`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ prompts: toSave }),
          signal: controller.signal,
        });

        if (!resp.ok) throw new Error("Failed to save sample prompts");

        confirmedRef.current = toSave;

        setSaveStates((prev) => {
          const next = [...prev];
          for (const i of changedIndices) {
            if (i < next.length) next[i] = "saved";
          }
          return next;
        });

        mutate((current) => {
          if (!current || current.id !== dataset.id) return current;
          return { ...current, samplePrompts: toSave };
        });
      } catch (e) {
        if ((e as Error).name === "AbortError") return;
        console.error(e);
        showError("Unable to save sample prompts");
        // Reset saving states back to idle on error
        setSaveStates((prev) => {
          const next = [...prev];
          for (const i of changedIndices) {
            if (i < next.length) next[i] = "idle";
          }
          return next;
        });
      }
    },
    [dataset, mutate, showError]
  );

  const debounceSave = useCallback(
    (newPrompts: string[]) => {
      if (timerRef.current) clearTimeout(timerRef.current);

      const confirmed = confirmedRef.current;
      const changedIndices: number[] = [];
      for (let i = 0; i < newPrompts.length; i++) {
        if (newPrompts[i] !== confirmed[i]) changedIndices.push(i);
      }

      if (changedIndices.length === 0) return;

      timerRef.current = setTimeout(() => {
        save(newPrompts, changedIndices);
      }, DEBOUNCE_MS);
    },
    [save]
  );

  const handleChange = (index: number, value: string) => {
    const updated = [...prompts];
    updated[index] = value;
    setPrompts(updated);

    // Clear any saved/saving indicator when typing
    setSaveStates((prev) => {
      const next = [...prev];
      next[index] = "idle";
      return next;
    });

    debounceSave(updated);
  };

  const handleAdd = () => {
    if (prompts.length >= MAX_PROMPTS) return;
    const updated = [...prompts, ""];
    setPrompts(updated);
    setSaveStates((prev) => [...prev, "idle"]);
  };

  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const confirmDelete = async () => {
    if (deleteIndex === null || !dataset) return;

    if (timerRef.current) clearTimeout(timerRef.current);

    const updated = prompts.filter((_, i) => i !== deleteIndex);

    setDeleting(true);
    try {
      const resp = await fetch(`${VITE_CATALOG_API}/datasets/${dataset.id}/sample-prompts`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompts: updated }),
      });

      if (!resp.ok) throw new Error("Failed to delete sample prompt");

      confirmedRef.current = updated;
      setPrompts(updated);
      setSaveStates((prev) => prev.filter((_, i) => i !== deleteIndex));
      mutate((current) => {
        if (!current || current.id !== dataset.id) return current;
        return { ...current, samplePrompts: updated };
      });
      setDeleteIndex(null);
    } catch (e) {
      console.error(e);
      showError("Unable to delete sample prompt");
    }
    setDeleting(false);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      abortRef.current?.abort();
    };
  }, []);

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        Write up to 6 example prompts that demonstrate what users can ask and do with this dataset.
        These will appear at the start of new agent conversations.
      </p>
      <div className="flex flex-col gap-4">
        {prompts.map((prompt, index) => (
          <SamplePromptRow
            key={index}
            index={index}
            value={prompt}
            saveState={saveStates[index] ?? "idle"}
            disabled={!isOwner}
            onChange={(value) => handleChange(index, value)}
            onDelete={() => setDeleteIndex(index)}
          />
        ))}
      </div>
      {isOwner && prompts.length < MAX_PROMPTS && (
        <div>
          <Button variant="secondary" size="sm" onClick={handleAdd}>
            <Plus className="size-4 mr-1" />
            Add Prompt
          </Button>
        </div>
      )}
      <AlertDialog
        open={deleteIndex !== null}
        onOpenChange={(open) => !deleting && !open && setDeleteIndex(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Prompt</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this sample prompt?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} onClick={() => setDeleteIndex(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={deleting}
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting && <Loader2 className="size-4 mr-1 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SamplePromptsTab;
